import React from 'react';
import { Chip } from '@material-ui/core';
import GrabFoodIcon from 'assets/icons/grabfood.png';
import FoodPandaIcon from 'assets/icons/foodpanda.png';

/**
 * Get the delivery platform icon based on the platform name
 * @param deliveryPlatform - The delivery platform name (GrabFood, FoodPanda, ShopeeFood)
 * @returns The icon path or null if not found
 */
export const getDeliveryPlatformIcon = (
	deliveryPlatform: string | null | undefined,
): string | null => {
	if (!deliveryPlatform) return null;

	switch (deliveryPlatform) {
		case 'GrabFood':
			return GrabFoodIcon;
		case 'FoodPanda':
			return FoodPandaIcon;
		case 'ShopeeFood':
			// If ShopeeFood icon exists, use it; otherwise return null or a placeholder
			return null; // You can add ShopeeFood icon later
		default:
			return null;
	}
};

/**
 * Get the Material-UI Chip color prop based on GrabFood order state
 * @param grabOrderState - The GrabFood order state
 * @returns The Material-UI Chip color prop value
 */
export const getGrabOrderStateColor = (
	grabOrderState: string | null | undefined,
): 'default' | 'primary' | 'secondary' => {
	if (!grabOrderState) return 'default';

	const state = grabOrderState.toUpperCase();

	switch (state) {
		case 'NEW':
		case 'ACCEPTED':
		case 'DRIVER_ALLOCATED':
		case 'BILL_PAID':
			return 'primary'; // Blue
		case 'PREPARING':
		case 'PREPARED':
			return 'default'; // Gray
		case 'READY_FOR_PICKUP':
		case 'READY':
		case 'ASSIGNED':
		case 'PICKED_UP':
		case 'ON_THE_WAY':
		case 'DRIVER_ARRIVED':
		case 'COLLECTED':
			return 'secondary'; // Orange/Pink
		case 'DELIVERED':
		case 'COMPLETED':
		case 'REFUNDED':
		case 'CANCELLED':
		case 'FAILED':
			return 'default'; // Custom style for these
		default:
			return 'default';
	}
};

/**
 * Get custom styles for the GrabFood order state chip
 * @param grabOrderState - The GrabFood order state
 * @returns Custom style object for the chip
 */
export const getGrabOrderStateStyle = (
	grabOrderState: string | null | undefined,
): React.CSSProperties => {
	if (!grabOrderState) return {};

	const state = grabOrderState.toUpperCase();

	switch (state) {
		case 'DELIVERED':
		case 'COMPLETED':
			return {
				backgroundColor: '#e8f5e9',
				borderColor: '#4caf50',
				color: '#2e7d32',
			}; // Light green background, green border, dark green text
		case 'REFUNDED':
			return {
				backgroundColor: '#fff3e0',
				borderColor: '#ff9800',
				color: '#e65100',
			}; // Light orange background, orange border, dark orange text
		case 'CANCELLED':
		case 'FAILED':
			return {
				backgroundColor: '#ffebee',
				borderColor: '#f44336',
				color: '#c62828',
			}; // Light red background, red border, dark red text
		case 'DRIVER_ALLOCATED':
		case 'BILL_PAID':
			return {
				backgroundColor: '#e3f2fd',
				borderColor: '#2196f3',
				color: '#1565c0',
			}; // Light blue background, blue border, dark blue text
		case 'DRIVER_ARRIVED':
		case 'COLLECTED':
			return {
				backgroundColor: '#fff3e0',
				borderColor: '#ff9800',
				color: '#e65100',
			}; // Light orange background, orange border, dark orange text
		default:
			return {};
	}
};

/**
 * Reusable component to display GrabFood order state as a Chip
 * @param grabOrderState - The GrabFood order state to display
 * @param size - The size of the chip (default: 'small')
 * @param variant - The variant of the chip (default: 'outlined')
 * @param style - Additional custom styles to apply
 */
interface GrabFoodOrderStateChipProps {
	grabOrderState: string | null | undefined;
	size?: 'small' | 'medium';
	variant?: 'outlined' | 'default';
	style?: React.CSSProperties;
}

export const GrabFoodOrderStateChip: React.FC<GrabFoodOrderStateChipProps> = ({
	grabOrderState,
	size = 'small',
	variant = 'outlined',
	style = {},
}) => {
	if (!grabOrderState) return null;

	return (
		<Chip
			label={grabOrderState}
			size={size}
			variant={variant}
			color={getGrabOrderStateColor(grabOrderState)}
			style={{
				height: '18px',
				fontSize: '10px',
				...getGrabOrderStateStyle(grabOrderState),
				...style,
			}}
		/>
	);
};

/**
 * Reusable component to display delivery platform icon
 * @param deliveryPlatform - The delivery platform name
 * @param iconStyle - Custom styles for the icon
 */
interface DeliveryPlatformIconProps {
	deliveryPlatform: string | null | undefined;
	iconStyle?: React.CSSProperties;
}

export const DeliveryPlatformIcon: React.FC<DeliveryPlatformIconProps> = ({
	deliveryPlatform,
	iconStyle = {},
}) => {
	const iconPath = getDeliveryPlatformIcon(deliveryPlatform);
	if (!iconPath) return null;

	return (
		<img
			src={iconPath}
			style={{
				width: '16px',
				height: '16px',
				marginRight: '4px',
				verticalAlign: 'middle',
				...iconStyle,
			}}
			alt={deliveryPlatform || 'Delivery Platform'}
		/>
	);
};

